import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

gsap?.registerPlugin(ScrollTrigger);

const PricingSection = () => {
  const [language, setLanguage] = useState('en');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const sectionRef = useRef(null);
  const plansRef = useRef([]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const ctx = gsap?.context(() => {
      plansRef?.current?.forEach((plan, index) => {
        if (plan) {
          gsap?.fromTo(
            plan,
            { y: 50, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.8,
              delay: index * 0.1,
              scrollTrigger: {
                trigger: plan,
                start: 'top 80%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });
    }, sectionRef);

    return () => ctx?.revert();
  }, []);

  const content = {
    en: {
      title: 'Simple, Transparent Pricing',
      subtitle: 'Choose the perfect plan for your business size and needs',
      monthly: 'Monthly',
      yearly: 'Yearly',
      yearlyDiscount: 'Save 20%',
      plans: [
        {
          id: 'starter',
          name: 'Starter',
          description: 'Perfect for small businesses just getting started',
          monthlyPrice: 29,
          yearlyPrice: 279,
          currency: '$',
          popular: false,
          features: [
            'Up to 100 bookings/month',
            '2 staff members',
            'Basic calendar integration',
            'Email reminders',
            'Telegram integration',
            'Basic analytics',
            'Email support',
          ],
          limitations: [
            'No WhatsApp integration',
            'No Instagram integration',
            'Limited customization',
          ],
        },
        {
          id: 'standard',
          name: 'Standard',
          description: 'Most popular choice for growing businesses',
          monthlyPrice: 79,
          yearlyPrice: 759,
          currency: '$',
          popular: true,
          features: [
            'Up to 500 bookings/month',
            '5 staff members',
            'Advanced calendar sync',
            'Smart AI reminders',
            'All channel integrations',
            'Advanced analytics',
            'Priority support',
            'Custom branding',
            'API access',
          ],
          limitations: [],
        },
        {
          id: 'advanced',
          name: 'Advanced',
          description: 'For established businesses with complex needs',
          monthlyPrice: 149,
          yearlyPrice: 1429,
          currency: '$',
          popular: false,
          features: [
            'Up to 2000 bookings/month',
            '15 staff members',
            'Multi-location support',
            'Advanced AI automation',
            'All integrations + custom',
            'Real-time analytics',
            'Dedicated support',
            'White-label solution',
            'Advanced API',
            'Custom workflows',
          ],
          limitations: [],
        },
        {
          id: 'custom',
          name: 'Enterprise',
          description: 'Tailored solution for large organizations',
          monthlyPrice: null,
          yearlyPrice: null,
          currency: '$',
          popular: false,
          features: [
            'Unlimited bookings',
            'Unlimited staff',
            'Enterprise integrations',
            'Custom AI training',
            'Dedicated infrastructure',
            'Advanced security',
            '24/7 phone support',
            'Custom development',
            'SLA guarantee',
            'On-premise option',
          ],
          limitations: [],
        },
      ],
      cta: {
        bookDemo: 'Book Demo',
        getStarted: 'Get Started',
        contactSales: 'Contact Sales',
      },
    },
    fa: {
      title: 'قیمت‌گذاری ساده و شفاف',
      subtitle: 'طرح مناسب برای اندازه و نیازهای کسب‌وکار خود را انتخاب کنید',
      monthly: 'ماهانه',
      yearly: 'سالانه',
      yearlyDiscount: '20% صرفه‌جویی',
      plans: [
        {
          id: 'starter',
          name: 'شروع',
          description: 'مناسب برای کسب‌وکارهای کوچک تازه شروع کننده',
          monthlyPrice: 29,
          yearlyPrice: 279,
          currency: '$',
          popular: false,
          features: [
            'تا 100 رزرو در ماه',
            '2 عضو کارکنان',
            'یکپارچگی تقویم پایه',
            'یادآوری ایمیل و پیامک',
            'یکپارچگی واتساپ',
            'تجزیه و تحلیل پایه',
            'پشتیبانی ایمیل',
          ],
          limitations: [
            'بدون یکپارچگی تلگرام',
            'بدون یکپارچگی اینستاگرام',
            'شخصی‌سازی محدود',
          ],
        },
        {
          id: 'standard',
          name: 'استاندارد',
          description: 'محبوب‌ترین انتخاب برای کسب‌وکارهای در حال رشد',
          monthlyPrice: 79,
          yearlyPrice: 759,
          currency: '$',
          popular: true,
          features: [
            'تا 500 رزرو در ماه',
            '5 عضو کارکنان',
            'همگام‌سازی پیشرفته تقویم',
            'یادآوری‌های هوشمند هوش مصنوعی',
            'یکپارچگی همه کانال‌ها',
            'تجزیه و تحلیل پیشرفته',
            'پشتیبانی اولویت‌دار',
            'برندسازی سفارشی',
            'دسترسی API',
          ],
          limitations: [],
        },
        {
          id: 'advanced',
          name: 'پیشرفته',
          description: 'برای کسب‌وکارهای تثبیت شده با نیازهای پیچیده',
          monthlyPrice: 149,
          yearlyPrice: 1429,
          currency: '$',
          popular: false,
          features: [
            'تا 2000 رزرو در ماه',
            '15 عضو کارکنان',
            'پشتیبانی چند مکان',
            'خودکارسازی پیشرفته هوش مصنوعی',
            'همه یکپارچگی‌ها + سفارشی',
            'تجزیه و تحلیل زمان واقعی',
            'پشتیبانی اختصاصی',
            'راه‌حل برچسب سفید',
            'API پیشرفته',
            'گردش‌های کاری سفارشی',
          ],
          limitations: [],
        },
        {
          id: 'custom',
          name: 'سازمانی',
          description: 'راه‌حل متناسب برای سازمان‌های بزرگ',
          monthlyPrice: null,
          yearlyPrice: null,
          currency: '$',
          popular: false,
          features: [
            'رزروهای نامحدود',
            'کارکنان نامحدود',
            'یکپارچگی‌های سازمانی',
            'آموزش سفارشی هوش مصنوعی',
            'زیرساخت اختصاصی',
            'امنیت پیشرفته',
            'پشتیبانی تلفنی 24/7',
            'توسعه سفارشی',
            'تضمین SLA',
            'گزینه داخلی',
          ],
          limitations: [],
        },
      ],
      cta: {
        bookDemo: 'درخواست دمو',
        getStarted: 'شروع کنید',
        contactSales: 'تماس با فروش',
      },
    },
  };

  const currentContent = content?.[language];

  const getPrice = (plan) => {
    if (!plan?.monthlyPrice) return 'Custom';
    const price =
      billingCycle === 'monthly' ? plan?.monthlyPrice : plan?.yearlyPrice;
    return `${plan?.currency}${price}`;
  };

  const getPeriod = () => {
    return billingCycle === 'monthly' ? '/mo' : '/yr';
  };

  return (
    <section ref={sectionRef} id="pricing" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div
          className={`text-center mb-16 ${language === 'fa' ? 'rtl' : 'ltr'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {currentContent?.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {currentContent?.subtitle}
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex bg-white rounded-lg p-1 shadow-md">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md transition-all duration-300 ${
                billingCycle === 'monthly'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}>
              {currentContent?.monthly}
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-md transition-all duration-300 relative ${
                billingCycle === 'yearly'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}>
              {currentContent?.yearly}
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                {currentContent?.yearlyDiscount}
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {currentContent?.plans?.map((plan, index) => (
            <div
              key={plan?.id}
              ref={(el) => (plansRef.current[index] = el)}
              className={`relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 ${
                plan?.popular ? 'ring-2 ring-blue-500 scale-105' : ''
              } ${hoveredPlan === plan?.id ? 'transform -translate-y-2' : ''}`}
              onMouseEnter={() => setHoveredPlan(plan?.id)}
              onMouseLeave={() => setHoveredPlan(null)}>
              {/* Popular Badge */}
              {plan?.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Plan Header */}
                <div
                  className={`text-center mb-6 ${
                    language === 'fa' ? 'rtl' : 'ltr'
                  }`}>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {plan?.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {plan?.description}
                  </p>

                  <div className="mb-4">
                    {plan?.monthlyPrice ? (
                      <>
                        <span className="text-4xl font-bold text-gray-900">
                          {getPrice(plan)}
                        </span>
                        <span className="text-gray-600">{getPeriod()}</span>
                        {billingCycle === 'yearly' && (
                          <div className="text-sm text-green-600 mt-1">
                            Save ${plan?.monthlyPrice * 12 - plan?.yearlyPrice}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-4xl font-bold text-gray-900">
                        Custom
                      </span>
                    )}
                  </div>
                </div>

                {/* Features List */}
                <div className="space-y-3 mb-8">
                  {plan?.features?.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start">
                      <Icon
                        name="Check"
                        size={16}
                        color="#059669"
                        className={`${
                          language === 'fa' ? 'ml-3 mt-1' : 'mr-3 mt-1'
                        } flex-shrink-0`}
                      />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}

                  {plan?.limitations?.map((limitation, limitIndex) => (
                    <div
                      key={limitIndex}
                      className="flex items-start opacity-60">
                      <Icon
                        name="X"
                        size={16}
                        color="#EF4444"
                        className={`${
                          language === 'fa' ? 'ml-3 mt-1' : 'mr-3 mt-1'
                        } flex-shrink-0`}
                      />
                      <span className="text-gray-500 text-sm">
                        {limitation}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <div className="space-y-3">
                  {plan?.id === 'custom' ? (
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full border-2 border-gray-300 hover:border-blue-500 hover:text-blue-600"
                      iconName="Phone"
                      iconPosition="left">
                      {currentContent?.cta?.contactSales}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant={plan?.popular ? 'default' : 'outline'}
                        size="lg"
                        className={`w-full ${
                          plan?.popular
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
                        }`}
                        iconName="ArrowRight"
                        iconPosition="right">
                        {currentContent?.cta?.getStarted}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-gray-600 hover:text-blue-600"
                        iconName="Calendar"
                        iconPosition="left">
                        {currentContent?.cta?.bookDemo}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-600 mb-6">
            {language === 'en'
              ? "Not sure which plan is right for you? Let's talk!"
              : 'مطمئن نیستید کدام طرح برای شما مناسب است؟ بیایید صحبت کنیم!'}
          </p>
          <Button
            variant="outline"
            size="lg"
            className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
            iconName="MessageCircle"
            iconPosition="left">
            {language === 'en' ? 'Schedule Consultation' : 'برنامه‌ریزی مشاوره'}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
